import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { AppState } from '@nxsys/store';

const APP_NAME = 'Nxsys';

/**
 * Static route-to-title mapping.
 * For parameterized routes (e.g. /agency/:id), we match by prefix in getTitle().
 */
const routeTitleMap: Record<string, string> = {
  // Auth
  '/signin': 'Sign In',
  '/forget-password': 'Forgot Password',

  // Dashboard
  '/dashboards': 'Dashboard',
  '/analytics': 'Analytics',

  // Employees
  '/employees-subcontractors': 'Employees',
  '/employees': 'Employees',

  // Profile (employee)
  '/general': 'Profile',

  // Payslip
  '/payslip': 'Payslips',
  '/payslips': 'Payslip Detail',

  // Payroll
  '/payroll': 'Payroll',

  // Prospects
  '/prospects': 'Prospects',

  // Agency
  '/agency': 'Agencies',

  // Assignments
  '/assignments': 'Assignments',

  // HMRC Update
  '/hmrc-update/p6-p9': 'P6/P9 Updates',
  '/hmrc-update/student-loan': 'Student Loan Updates',

  // Bulk Update
  '/bulk-update': 'Bulk Update',
  '/bulk-update/download-data': 'Download Data',

  // Statutory
  '/statutory-leave': 'Statutory Leave',
  '/statutory-payment': 'Statutory Payments',

  // Payment Settings
  '/payments': 'Payments',
  '/deductions': 'Deductions',
  '/payment-rates': 'Payment Rates',
  '/vat-codes': 'VAT Codes',

  // Timesheets
  '/timesheets': 'Timesheets',
  '/timesheets/add-new': 'New Timesheet',
  '/timesheets/pending': 'Pending Agency Approval',
  '/timesheets/generate-draft-payroll': 'Generate Draft Payroll',
  '/timesheets/awaiting-invoice-generation': 'Awaiting Invoice Generation',
  '/timesheets-agency': 'Timesheets',
  '/timesheets-employee': 'Timesheets',
  '/timesheets-employee/add-new': 'New Timesheet',

  // Invoices
  '/invoices': 'Invoices',
  '/invoices/create-invoice': 'Create Invoice',
  '/invoices/invoice': 'Invoice Detail',
  '/invoices/credit-notes': 'Credit Notes',
  '/invoices/create-invoice-credit': 'Create Credit Note',
  '/invoices/credit-note': 'Credit Note Detail',

  // Reports
  '/reports': 'Reports',
  '/reports/payroll/draft-payroll-summary': 'Draft Payroll Summary',
  '/reports/payroll/draft-payroll-payee-umbrella-summary':
    'Draft Payroll Summary',
  '/reports/payroll/cis-payment-summary': 'CIS Payment Summary',
  '/reports/payroll/payment-summary': 'Payment Summary',
  '/reports/payroll/holiday-pay-balance-detail': 'Holiday Pay Balance Detail',
  '/reports/payroll/holiday-pay-balance-summary': 'Holiday Pay Balance Summary',
  '/reports/payroll/net-pay-report': 'Net Pay Report',
  '/reports/payroll/52-week-average-summary-report': '52 Week Average Summary',
  '/reports/payroll/p30': 'P30 Report',
  '/reports/payroll/p32': 'P32 Report',
  '/reports/pension/contribution-report': 'Pension Contribution Report',
  '/reports/pension/auto-enrolment-status-report':
    'Auto Enrolment Status Report',
  '/reports/pension/contributions-ireland': 'Pension Contributions Ireland',
  '/reports/management/employment-intermediary':
    'Employment Intermediary Report',
  '/reports/management/earning-summary': 'Earning Summary Report',
  '/reports/management/dormant-worker': 'Dormant Worker Report',
  '/reports/bank-files': 'Bank Files',
  '/reports/employee/right-to-work': 'Right to Work Report',
  '/reports/employee/awr': 'AWR Report',
  '/reports/employee/wtr': 'WTR Report',
  '/reports/agency/employment-intermediary': 'Employment Intermediary Report',
  '/reports/agency/awr': 'AWR Report',
  '/reports/agency/wtr': 'WTR Report',
  '/reports/attachment-orders-report': 'Attachment Orders Report',
  '/reports/on-hold-worker': 'On Hold Worker Report',

  // Documents
  '/documents': 'Documents',
  '/agency-documents': 'Agency Documents',
  '/employee-documents': 'Documents',

  // Import
  '/import/agencies': 'Import Agencies',
  '/import/employees-subcontractors': 'Import Employees',
  '/import/prospects': 'Import Prospects',
  '/import/timesheets': 'Import Timesheets',

  // Export
  '/export': 'Export',

  // KID
  '/kid': 'KID',

  // RTI Filings
  '/rti-filings/fps': 'FPS Filings',
  '/rti-filings/eps': 'EPS Filings',
  '/rti-filings/cis-300': 'CIS 300 Filings',

  // Revenue Filings (Ireland)
  '/revenue-filings/psr': 'PSR Filings',
  '/revenue-filings/submit-psr': 'Submit PSR',

  // RPN (Ireland)
  '/rpn': 'RPN',

  // AEPN (Ireland)
  '/aepn': 'AEPN',

  // Submit PSR (Ireland)
  '/submit-psr': 'Submit PSR',

  // Activity Logs
  '/activity-log': 'Activity Logs',

  // Permissions
  '/permissions': 'Permissions',
  '/permissions/company': 'Employer Permissions',
  '/permissions/employees': 'Employee Permissions',
  '/permissions/candidates': 'Candidate Permissions',
  '/permissions/agency': 'Agency Permissions',

  // Settings
  '/settings': 'Settings',

  // Notifications & Messages
  '/notifications': 'Notifications',
  '/messages': 'Messages',

  // Change Password
  '/change-password': 'Change Password',

  // Account security — getTitle walks up parent paths, so this one entry also
  // titles /account-security/password and /account-security/two-factor.
  '/account-security': 'Account Security',

  // Super Admin
  '/manage-account': 'Manage Account',

  // Company Draft
  '/create-company': 'Create Company',

  // Error pages
  '/error-pages/error-401': 'Unauthorized',
  '/error-pages/error-403': 'Forbidden',
  '/error-pages/error-404': 'Not Found',
  '/error-pages/error-500': 'Server Error',
  '/error-pages/error-503': 'Service Unavailable',
  '/error-pages/coming-soon': 'Coming Soon',
  '/error-pages/maintenance': 'Maintenance',
};

/**
 * Prefix-based patterns for parameterized routes.
 * Ordered from most specific to least specific.
 */
const prefixTitleMap: [string, string][] = [
  // Detail pages (more specific paths first)
  ['/invoices/invoice/', 'Invoice Detail'],
  ['/invoices/credit-note/', 'Credit Note Detail'],
  ['/reports/payroll/', 'Payroll Report'],
  ['/rti-filings/fps/', 'FPS Detail'],
  ['/rti-filings/eps/', 'EPS Detail'],
  ['/rti-filings/cis-300/', 'CIS 300 Detail'],
  ['/payroll/', 'Payroll Detail'],
  ['/companies/', 'Company Detail'],
  ['/employees-subcontractors/', 'Employee Detail'],
  ['/agency/', 'Agency Detail'],
  ['/assignments/', 'Assignment Detail'],
  ['/bulk-update/', 'Bulk Update Detail'],
  ['/timesheets-agency/', 'Timesheet Detail'],
  ['/timesheets-employee/', 'Timesheet Detail'],
  ['/timesheets/', 'Timesheet Detail'],
  ['/payslips/', 'Payslip Detail'],
  ['/statutory-leave/', 'Statutory Leave Detail'],
  ['/statutory-payment/', 'Statutory Payment Detail'],
  ['/manage-account/', 'Manage Account'],
  ['/create-company/', 'Create Company'],
];

const bureauTitleOverrides: Record<string, [string, string]> = {
  '/companies': ['Employers', 'Companies'],
};

const bureauPrefixOverrides: [string, [string, string]][] = [
  ['/companies/', ['Employer Detail', 'Company Detail']],
];

function getTitle(pathname: string, isBureau: boolean): string {
  if (bureauTitleOverrides[pathname]) {
    return isBureau
      ? bureauTitleOverrides[pathname][0]
      : bureauTitleOverrides[pathname][1];
  }
  if (routeTitleMap[pathname]) {
    return routeTitleMap[pathname];
  }

  for (const [prefix, titles] of bureauPrefixOverrides) {
    if (pathname.startsWith(prefix)) {
      return isBureau ? titles[0] : titles[1];
    }
  }
  for (const [prefix, title] of prefixTitleMap) {
    if (pathname.startsWith(prefix)) {
      return title;
    }
  }

  const segments = pathname.split('/').filter(Boolean);
  while (segments.length > 1) {
    segments.pop();
    const parentPath = '/' + segments.join('/');
    if (bureauTitleOverrides[parentPath]) {
      return isBureau
        ? bureauTitleOverrides[parentPath][0]
        : bureauTitleOverrides[parentPath][1];
    }
    if (routeTitleMap[parentPath]) {
      return routeTitleMap[parentPath];
    }
  }

  return '';
}

const usePageTitle = () => {
  const { pathname } = useLocation();
  const { isBureau } = useSelector((state: AppState) => state.appStore);

  useEffect(() => {
    const title = getTitle(pathname, isBureau);
    document.title = title ? `${title} | ${APP_NAME}` : APP_NAME;
  }, [pathname, isBureau]);
};

export default usePageTitle;
